import type { EventHookOn, EventHookTrigger } from '@vueuse/core'

import { useRouteQuery } from '@vueuse/router'

import type { SubscriptionPaymentPeriodUnit } from '~/types'

import type { SubscriptionPaymentMethod } from './api/subscription/useFetchSubscription'
import type { PriceListAddon, PriceListItem, PriceListItemPrice } from './api/price-list/useFetchPriceList'

type Stage = 'plans' | 'cart' | 'final'

type PromotedPlan = {
  id: string
  text: string
  info?: string
} | undefined

export interface PlanSelectedParams {
  plan: string
  price: number
  priceId: PriceListItemPrice['id']
  currency?: string
}

export interface ActivationFailureParams {
  planPriceId: PriceListItemPrice['id']
  errorMessage: 'Unknown error' | 'Invalid VAT number'
}

export interface ActivationCompletedParams {
  plan: string
  couponId?: string
  currency?: string
  annualPrice: number
  planPriceId: PriceListItemPrice['id']
  paymentMethod: SubscriptionPaymentMethod
}

export interface SubscriptionAddon {
  addonId: PriceListAddon['addon_id'] | (string & {})
  quantity: number
}

interface SubscriptionModalContext {
  addons: Ref<SubscriptionAddon[]>
  selectedAddonsForEstimate: ComputedRef<SubscriptionAddon[]>
  availablePaymentPeriodUnits: ComputedRef<SubscriptionPaymentPeriodUnit[]>
  paymentPeriodUnit: Ref<SubscriptionPaymentPeriodUnit>
  selectedPlan: Ref<PriceListItem | undefined>
  stage: ComputedRef<Stage>
  priceList: ReturnType<typeof usePriceList>
  discountCode: Ref<string>
  promotedPlan: ComputedRef<PromotedPlan>
  planSelected: EventHookTrigger<PlanSelectedParams>
  isInterestedInLegalOnboarding: Ref<boolean>
  onPlanSelected: EventHookOn<PlanSelectedParams>
  activationFailed: EventHookTrigger<ActivationFailureParams>
  onActivationFailed: EventHookOn<ActivationFailureParams>
  activationCompleted: EventHookTrigger<ActivationCompletedParams>
  onActivationCompleted: EventHookOn<ActivationCompletedParams>
  notifyContactFormTracker: EventHookTrigger<any>
  onNotifyContactFormTracker: EventHookOn<any>
  showIntercom: EventHookTrigger<any>
  onShowIntercom: EventHookOn<any>
}

const SUBSCRIPTION_MODAL_CONTEXT_KEY: InjectionKey<SubscriptionModalContext> = Symbol('subscription-modal-context')

export const useSubscriptionProcessContextProvider = ({
  priceListName = undefined,
}: {
  priceListName?: MaybeRef<string>
} = {}) => {
  const { t } = useI18n()
  const { hasValidSubscription } = useSubscription()
  const priceList = usePriceList({ name: priceListName })

  const paymentPeriodUnit = ref<SubscriptionPaymentPeriodUnit>('year')
  const selectedPlan = ref<PriceListItem>()

  const addons = ref<SubscriptionAddon[]>([])

  const selectedAddonsForEstimate = computed(() => addons.value.map(({ addonId, quantity }) => {
    const addon = priceList.dataWithoutUpgrades.value?.addons?.find(({ addon_id }) => addon_id === addonId)

    if (!addon)
      return undefined

    const priceId = addon.prices.find(({ period_unit }) => period_unit === paymentPeriodUnit.value)?.price_id

    if (!priceId)
      return undefined

    return { addonId: priceId, quantity }
  }).filter(Boolean) as SubscriptionAddon[])

  const isInterestedInLegalOnboarding = ref(false)

  watch(selectedPlan, (plan) => {
    if (!plan) {
      addons.value = []
    }
  })

  const discountCode = ref('')

  const stage = computed<Stage>(() => {
    if (hasValidSubscription.value && selectedPlan.value)
      return 'final'

    if (selectedPlan.value)
      return 'cart'

    return 'plans'
  })

  const availablePaymentPeriodUnits = computed(() => {
    const items = priceList.dataWithoutUpgrades.value?.items.filter(item => !item.data.custom_monthly_price) ?? []

    return (['year', 'month'] as SubscriptionPaymentPeriodUnit[]).reduce((available, unit) => {
      const allItemsContainPeriod = items.every(item =>
        item.prices.some(({ period_unit, period }) => period_unit === unit && period === 1))

      if (allItemsContainPeriod)
        return [...available, unit]

      return available
    }, [] as SubscriptionPaymentPeriodUnit[])
  })

  watch(availablePaymentPeriodUnits, (periods) => {
    /*
     * Prefer monthly payment period if price list is v2
     * for others prefer yearly payment period
     */
    if (periods.includes('month') && priceList.data.value?.data_version === 'v2')
      paymentPeriodUnit.value = 'month'
    else if (periods.includes('year'))
      paymentPeriodUnit.value = 'year'
    else
      paymentPeriodUnit.value = periods[0]
  }, { immediate: true })

  const templateFeatures = useRouteQuery(
    'template-features',
    '',
    { transform: val => val.split(',') },
  )

  const promotedPlan = computed<PromotedPlan>(() => {
    const promotedItem = priceList.dataWithoutUpgrades.value?.items.find(item =>
      templateFeatures.value?.some(feature =>
        item.entitlements.map(({ feature_id }) => feature_id).includes(feature),
      ),
    )

    const firstItem = priceList.dataWithoutUpgrades.value?.items[0]

    if (templateFeatures.value?.length && promotedItem && promotedItem.id !== firstItem?.id) {
      return {
        id: promotedItem.id,
        text: t('recommended'),
        info: t('subscribe.recommended-plan-info'),
      }
    }

    if (!firstItem)
      return undefined

    return {
      id: firstItem.id,
      text: t('popular'),
      info: undefined,
    }
  })

  const planSelectedHook = createEventHook<PlanSelectedParams>()
  const activationFailureHook = createEventHook<ActivationFailureParams>()
  const activationCompletedHook = createEventHook<ActivationCompletedParams>()
  const showIntercomHook = createEventHook()
  const notifyContactFormTracker = createEventHook()

  const toProvide: SubscriptionModalContext = {
    addons,
    selectedAddonsForEstimate,
    availablePaymentPeriodUnits,
    paymentPeriodUnit,
    selectedPlan,
    stage,
    priceList,
    discountCode,
    promotedPlan,
    isInterestedInLegalOnboarding,
    planSelected: planSelectedHook.trigger,
    onPlanSelected: planSelectedHook.on,
    activationFailed: activationFailureHook.trigger,
    onActivationFailed: activationFailureHook.on,
    activationCompleted: activationCompletedHook.trigger,
    onActivationCompleted: activationCompletedHook.on,
    showIntercom: showIntercomHook.trigger,
    onShowIntercom: showIntercomHook.on,
    onNotifyContactFormTracker: notifyContactFormTracker.on,
    notifyContactFormTracker: notifyContactFormTracker.trigger,
  }

  provide(SUBSCRIPTION_MODAL_CONTEXT_KEY, toProvide)

  return toProvide
}

export const useSubscriptionProcessContext = () => {
  const context = inject(SUBSCRIPTION_MODAL_CONTEXT_KEY)

  if (!context)
    throw new Error('useSubscriptionProcessContext must be used with useSubscriptionProcessContextProvider')

  return context
}
